import { setup } from 'xstate';

import { useMachine } from '@xstate/react';
import * as React from 'react';

type Point = {
  x: number;
  y: number;
};

function convertPixelToSvgCoord({ x, y }: Point, el: SVGGElement) {
  const svg = el.ownerSVGElement!;
  let p = svg.createSVGPoint();
  p.x = x;
  p.y = y;
  p = p.matrixTransform(svg.getScreenCTM()?.inverse());
  return p;
}

function clamp(lo: number, x: number, hi: number) {
  return x < lo ? lo : x > hi ? hi : x;
}

const machine = setup({
  types: {} as {
    context: {
      initial?: {
        elSvg: SVGElement;
        el: SVGGElement;
        dx: number;
        dy: number;
      };
      x: number;
      y: number;
    };
    events:
      | {
          type: 'pointerdown';
          event: PointerEvent;
          el: SVGGElement;
        }
      | {
          type: 'pointermove';
          event: PointerEvent;
        }
      | {
          type: 'pointerup';
          event: PointerEvent;
        };
  },
  actions: {},
}).createMachine({
  id: 'Pointer events',
  initial: 'not_dragging',
  context: {
    x: 30,
    y: 30,
  },
  states: {
    not_dragging: {
      on: {
        pointerdown: {
          target: 'dragging',
          actions: [
            ({ context, event: { el, event } }) => {
              if (event.button === 0) {
                // only on left button

                const elSvg = el.ownerSVGElement!;
                const { x: eventX, y: eventY } = convertPixelToSvgCoord(event, el);
                const { x, y } = context;
                const dx = eventX - x;
                const dy = eventY - y;
                console.log({ eventX, eventY });
                context.initial = { elSvg, el, dx, dy };
                el.setPointerCapture(event.pointerId);
              }
            },
          ],
        },
      },
    },
    dragging: {
      on: {
        pointermove: {
          actions: [
            ({ context, event: { event } }) => {
              const { initial } = context;
              if (initial) {
                const { elSvg, el, dx, dy } = initial;
                const parentRect = elSvg.getBoundingClientRect();
                const domEventX = clamp(parentRect.left, event.clientX, parentRect.right);
                const domEventY = clamp(parentRect.top, event.clientY, parentRect.bottom);
                const { x: eventX, y: eventY } = convertPixelToSvgCoord({ x: domEventX, y: domEventY }, el);
                const x = eventX - dx;
                const y = eventY - dy;
                context.x = x;
                context.y = y;
                console.log('mousedown', { parentRect, event, x, y, dx, dy });
                el.setAttribute('transform', `translate(${x}, ${y})`);
              }
            },
          ],
        },
        pointerup: {
          target: 'not_dragging',
          actions: [
            ({ context }) => {
              context.initial = undefined;
            },
          ],
        },
      },
    },
  },
});

function ByPointerEventExt() {
  const [state, send] = useMachine(machine);
  const refG = React.useRef<SVGGElement>(null);
  React.useEffect(() => {
    const el = refG.current!;
    function pointerdown(event: PointerEvent) {
      send({ type: 'pointerdown', event, el });
    }
    function pointermove(event: PointerEvent) {
      send({ type: 'pointermove', event });
    }
    function pointerup(event: PointerEvent) {
      send({ type: 'pointerup', event });
    }
    function preventDefault(e: Event) {
      e.preventDefault();
    }
    el.addEventListener('pointerdown', pointerdown);
    el.addEventListener('pointermove', pointermove);
    el.addEventListener('pointerup', pointerup);
    el.addEventListener('pointercancel', pointerup);
    el.addEventListener('touchstart', preventDefault);
    return () => {
      el.removeEventListener('pointerdown', pointerdown);
      el.removeEventListener('pointermove', pointermove);
      el.removeEventListener('pointerup', pointerup);
      el.removeEventListener('pointercancel', pointerup);
      el.removeEventListener('touchstart', preventDefault);
    };
  }, []);
  // console.log('MouseOnlyXState', { state });
  return (
    <div className="container rounded border border-current relative mb-10">
      <div className="absolute" style={{ left: '50%', transform: 'translateX(-50%)' }}>
        <div className="backdrop-brightness-90 p-1 rounded-md">{state.value}</div>
      </div>
      <svg viewBox="0 0 300 100">
        <g ref={refG} className="cursor-grab" transform="translate(30, 30)">
          <circle cx={0} cy={0} stroke="black" r="30"></circle>
          <g fontSize="14" textAnchor="middle" fill="white">
            <text dy="0.0em">Drag</text>
            <text dy="1.0em">me</text>
          </g>
        </g>
      </svg>
    </div>
  );
}

export default React.memo(ByPointerEventExt);
